import { useEffect, useRef, useCallback, useState } from "react";
import { getAccessToken } from "@/lib/api/auth";
import { loadRuntimeConfig } from "@/lib/runtime-config";

type WebSocketMessage = {
  type: string;
  [key: string]: unknown;
};

interface UseWebSocketOptions {
  onMessage: (message: WebSocketMessage) => void;
  enabled?: boolean;
}

/**
 * Resolve the configured WS URL against the current page origin.
 *
 * - A relative path (`/ws`) becomes same-origin `ws(s)://<page-host>/ws`, so
 *   the socket follows the page whether it's served at localhost or via the
 *   Cloudflare tunnel at https://dev.yuklar.com.
 * - An absolute `ws://` URL is upgraded to `wss://` on HTTPS pages so browsers
 *   don't block it as mixed content (keeps deploy configs human-readable).
 */
function resolveWsUrl(configured: string): string {
  if (typeof window === "undefined") return configured;
  const isHttps = window.location.protocol === "https:";
  if (configured.startsWith("/")) {
    const proto = isHttps ? "wss:" : "ws:";
    return `${proto}//${window.location.host}${configured}`;
  }
  return isHttps && configured.startsWith("ws://")
    ? "wss://" + configured.slice("ws://".length)
    : configured;
}

/**
 * WebSocket hook with automatic reconnection and authentication
 *
 * Features:
 * - Auto-connects on mount
 * - Sends auth token on connection
 * - Handles ping/pong heartbeat
 * - Exponential backoff reconnection (1s → 2s → 4s → max 30s)
 * - Auto-cleanup on unmount
 */
export function useWebSocket({
  onMessage,
  enabled = true,
}: UseWebSocketOptions) {
  const ws = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const isMounted = useRef(true);
  const connectRef = useRef<(() => void) | null>(null);
  const onMessageRef = useRef(onMessage);
  const [isConnected, setIsConnected] = useState(false);

  // Keep onMessageRef current without adding onMessage to connect's dep array
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const scheduleReconnect = useCallback(() => {
    if (!enabled || !isMounted.current) return;

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (max)
    const delay = Math.min(1000 * 2 ** reconnectAttempts.current, 30000);
    reconnectAttempts.current++;

    clearTimeout(reconnectTimeout.current);
    reconnectTimeout.current = setTimeout(() => connectRef.current?.(), delay);
  }, [enabled]);

  const connect = useCallback(() => {
    if (!enabled || !isMounted.current) return;

    const token = getAccessToken();
    if (!token) {
      console.warn("[WS] No access token, skipping connection");
      return;
    }

    loadRuntimeConfig()
      .then(({ wsUrl }) => {
        if (!enabled || !isMounted.current) return;

        ws.current = new WebSocket(resolveWsUrl(wsUrl));

        ws.current.onopen = () => {
          console.log("[WS] Connected");
          reconnectAttempts.current = 0;
          setIsConnected(true);

          // Send auth message
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({ type: "auth", token }));
          }
        };

        ws.current.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data) as WebSocketMessage;

            // Handle auth success
            if (message.type === "auth_success") {
              console.log("[WS] Authenticated successfully");
              return;
            }

            // Handle auth failure
            if (message.type === "auth_failure") {
              console.error("[WS] Authentication failed");
              ws.current?.close();
              return;
            }

            // Handle ping/pong heartbeat
            if (message.type === "ping") {
              ws.current?.send(JSON.stringify({ type: "pong" }));
              return;
            }

            // Pass message to callback
            onMessageRef.current(message);
          } catch (error) {
            console.error("[WS] Failed to parse message:", error);
          }
        };

        ws.current.onerror = (error) => {
          console.error("[WS] Error:", error);
        };

        ws.current.onclose = (event) => {
          console.log("[WS] Disconnected", event.code, event.reason);
          setIsConnected(false);

          // Only reconnect if component is still mounted and enabled
          if (isMounted.current && enabled) {
            scheduleReconnect();
          }
        };
      })
      .catch((error) => {
        console.error("[WS] Connection failed:", error);
        setIsConnected(false);
        scheduleReconnect();
      });
  }, [enabled, scheduleReconnect]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const disconnect = useCallback(() => {
    clearTimeout(reconnectTimeout.current);

    if (ws.current) {
      ws.current.onclose = null; // Prevent reconnection
      ws.current.close();
      ws.current = null;
    }
    setIsConnected(false);
  }, []);

  useEffect(() => {
    isMounted.current = true;

    if (enabled) {
      const timeoutId = setTimeout(() => {
        connect();
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        isMounted.current = false;
        disconnect();
      };
    }

    return () => {
      isMounted.current = false;
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  return {
    isConnected,
    disconnect,
  };
}
